import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';

import { CustomSelect } from '../CustomSelect';
import { LockedControl } from '../LockedControl';
import {
  FeedFilters,
  getGameShortName,
  getModeTrad,
  getWeaponTrad,
} from '../../helpers';
import { AuthReason } from '../../atoms';
import { Game } from '../../types';
import styles from './style';

interface FeedFilterBarProps {
  filters: FeedFilters;
  games: Game[];
  onChange: (filters: FeedFilters) => void;
  // A visitor can't filter: each select is locked and its click asks to log in.
  onLocked?: (reason: AuthReason) => void;
}

// Game, mode and weapon, one value each like the app. The mode and the weapon need a game:
// each game has its own modes and weapons.
const FeedFilterBar: React.FC<FeedFilterBarProps> = React.memo((props) => {
  const { filters, games, onChange, onLocked } = props;
  const { t } = useTranslation();

  const game = useMemo(() => games.find((g) => g.name === filters.gameName), [filters.gameName, games]);

  // The saved game is an option before the list of the games is loaded (no value out of the list).
  const gameOptions = useMemo(() => {
    if (!games.length && filters.gameName) {
      return [{ label: getGameShortName(filters.gameName), value: filters.gameName }];
    }
    return games.map((g) => ({ label: getGameShortName(g.name), value: g.name }));
  }, [filters.gameName, games]);

  const modeOptions = useMemo(() => (game ? game.modes.map((m) => ({ label: getModeTrad(m, t), value: m })) : []), [game, t]);

  const weaponOptions = useMemo(() => (game ? game.weapons.map((w) => ({ label: getWeaponTrad(w, t), value: w })) : []), [game, t]);

  const onChangeGame = useCallback((gameName: string) => {
    onChange({ ...filters, gameName, mode: '', weapon: '' });
  }, [filters, onChange]);

  const onChangeMode = useCallback((mode: string) => {
    onChange({ ...filters, mode });
  }, [filters, onChange]);

  const onChangeWeapon = useCallback((weapon: string) => {
    onChange({ ...filters, weapon });
  }, [filters, onChange]);

  const lockGame = useCallback(() => {
    if (onLocked) {
      onLocked('filterGame');
    }
  }, [onLocked]);

  const lockMode = useCallback(() => {
    if (onLocked) {
      onLocked('filterMode');
    }
  }, [onLocked]);

  const lockWeapon = useCallback(() => {
    if (onLocked) {
      onLocked('filterWeapon');
    }
  }, [onLocked]);

  if (onLocked) {
    return (
      <Box sx={styles.bar}>
        <LockedControl tooltip={t('admin.guest.reasons.filterGame.title')} onClick={lockGame} fullWidth>
          <CustomSelect label={t('admin.filters.game')} emptyLabel={t('admin.common.all')} options={[]} value="" onChange={onChangeGame} />
        </LockedControl>
        <LockedControl tooltip={t('admin.guest.reasons.filterMode.title')} onClick={lockMode} fullWidth>
          <CustomSelect label={t('admin.filters.mode')} emptyLabel={t('admin.common.all')} options={[]} value="" onChange={onChangeMode} />
        </LockedControl>
        <LockedControl tooltip={t('admin.guest.reasons.filterWeapon.title')} onClick={lockWeapon} fullWidth>
          <CustomSelect label={t('admin.filters.weapon')} emptyLabel={t('admin.common.allFeminine')} options={[]} value="" onChange={onChangeWeapon} />
        </LockedControl>
      </Box>
    );
  }

  return (
    <Box sx={styles.bar}>
      <CustomSelect
        label={t('admin.filters.game')}
        emptyLabel={t('admin.common.all')}
        options={gameOptions}
        value={filters.gameName}
        onChange={onChangeGame}
      />
      <CustomSelect
        label={t('admin.filters.mode')}
        emptyLabel={t('admin.common.all')}
        options={modeOptions}
        value={filters.mode}
        onChange={onChangeMode}
        disabled={!game}
      />
      <CustomSelect
        label={t('admin.filters.weapon')}
        emptyLabel={t('admin.common.allFeminine')}
        options={weaponOptions}
        value={filters.weapon}
        onChange={onChangeWeapon}
        disabled={!game}
      />
    </Box>
  );
});

export default FeedFilterBar;
